import React, { useState, useEffect } from 'react';
import { razorpayService, RazorpaySuccessResponse, RazorpayErrorResponse, RazorpayPlanSelection } from '../services/razorpayService';
import { formatCurrency } from '../utils/currencyDetection';
import { getRazorpayConfig, isRazorpayCheckoutSuppressed } from '../utils/paymentGatewayConfig';

interface RazorpayPaymentFormProps {
  /** Display-only; the backend prices the order from planSelection. */
  amount: number;
  currency: string;
  planSelection: RazorpayPlanSelection;
  customerName?: string;
  customerEmail?: string;
  customerContact?: string;
  description: string;
  onSuccess: (response: RazorpaySuccessResponse) => Promise<void>;
  onError: (error: string) => void;
  paymentMethod?: 'card' | 'upi' | 'all';
  className?: string;
}

const RazorpayPaymentForm: React.FC<RazorpayPaymentFormProps> = ({
  amount,
  currency,
  planSelection,
  customerName,
  customerEmail,
  customerContact,
  description,
  onSuccess,
  onError,
  paymentMethod = 'all',
  className = '',
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [upiId, setUpiId] = useState('');
  const [useUpiId, setUseUpiId] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (isRazorpayCheckoutSuppressed()) {
      setIsLoading(false);
      return;
    }
    const loadRazorpay = async () => {
      try {
        const loaded = await razorpayService.loadRazorpay();
        if (!loaded) {
          onError('Failed to load Razorpay SDK. Please refresh the page.');
        }
      } catch (error) {
        onError('Failed to initialize Razorpay');
      } finally {
        setIsLoading(false);
      }
    };

    loadRazorpay();
  }, [onError]);

  const handlePayment = async () => {
    setInlineError(null);

    if (paymentMethod === 'upi' && currency.toUpperCase() !== 'INR') {
      const message = 'UPI payments require INR. Please choose card payment for this checkout currency.';
      onError(message);
      setInlineError(message);
      return;
    }

    if (!razorpayService.isConfigured()) {
      const message = 'Razorpay is not configured. Please contact support.';
      onError(message);
      setInlineError(message);
      return;
    }

    setIsProcessing(true);

    try {
      // Create order on backend
      const order = await razorpayService.createOrder(planSelection, currency);

      // Use the key_id from the order response (must match the one used to create the order)
      // This is critical - the order must be opened with the same key_id that created it
      const razorpayKeyId = order.keyId || getRazorpayConfig().keyId;
      if (!razorpayKeyId) {
        throw new Error('Razorpay Key ID not configured. Please check your environment variables.');
      }

      // Log for debugging
      console.log('Razorpay checkout setup:', {
        orderId: order.orderId,
        amount: order.amount,
        currency: order.currency,
        keyId: razorpayKeyId.substring(0, 15) + '...',
        keyIdMatches: order.keyId ? 'Using backend key' : 'Using frontend key (fallback)',
      });

      // Prepare Razorpay options
      const options = {
        key: razorpayKeyId,
        amount: order.amount,
        currency: currency.toUpperCase(),
        name: 'Scubamail',
        description: description,
        order_id: order.orderId,
        handler: async (response: RazorpaySuccessResponse) => {
          try {
            // Verify payment signature
            const verified = await razorpayService.verifyPayment(
              response.razorpay_payment_id,
              response.razorpay_order_id,
              response.razorpay_signature
            );

            if (verified) {
              await onSuccess(response);
            } else {
              onError('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            onError('Failed to verify payment. Please contact support.');
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: customerName || '',
          email: customerEmail || '',
          contact: customerContact || '',
        },
        notes: {
          description: description,
        },
        theme: {
          color: '#7c3aed',
        },
        modal: {
          ondismiss: () => {
            console.log('Razorpay modal dismissed');
            setIsProcessing(false);
          },
        },
        method: {
          upi: paymentMethod === 'upi' || paymentMethod === 'all',
          card: paymentMethod === 'card' || paymentMethod === 'all',
          netbanking: paymentMethod === 'all',
          wallet: paymentMethod === 'all',
        },
      } as any;

      // Add UPI ID if provided
      if (useUpiId && upiId.trim()) {
        options.method = {
          ...options.method,
          upi: {
            flow: 'intent',
            vpa: upiId.trim(),
          },
        };
      }

      // Open Razorpay checkout
      await razorpayService.openCheckout(options);
    } catch (error) {
      console.error('Payment error:', error);
      const message = error instanceof Error ? error.message : 'Payment failed. Please try again.';
      onError(message);
      setInlineError(message);
      setIsProcessing(false);
    }
  };

  if (isRazorpayCheckoutSuppressed()) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2 text-gray-600">Loading Razorpay...</span>
      </div>
    );
  }

  if (!razorpayService.isConfigured()) {
    return (
      <div className={`p-8 text-center ${className}`}>
        <div className="text-gray-500 mb-4">
          <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-lg font-medium">Razorpay Not Configured</p>
          <p className="text-sm text-gray-400 mt-1">
            Please configure Razorpay keys to enable payments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* UPI ID Input (Optional) */}
      {paymentMethod === 'upi' || paymentMethod === 'all' ? (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="use-upi-id"
              checked={useUpiId}
              onChange={(e) => setUseUpiId(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="use-upi-id" className="text-sm text-gray-700">
              Pay with specific UPI ID
            </label>
          </div>
          {useUpiId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your UPI ID
              </label>
              <input
                type="text"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                placeholder="yourname@paytm"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter your UPI ID to pay directly, or leave empty to choose from your UPI apps
              </p>
            </div>
          )}
        </div>
      ) : null}

      {/* Payment Button */}
      <button
        type="button"
        onClick={handlePayment}
        disabled={isProcessing || (useUpiId && !upiId.trim())}
        className="w-full py-3 px-6 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            <span>Processing...</span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Pay {formatCurrency(amount, currency)}</span>
          </>
        )}
      </button>

      {inlineError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {inlineError}
        </div>
      )}

      {/* Payment Methods Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center mb-2">Accepted Payment Methods</p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {paymentMethod === 'card' || paymentMethod === 'all' ? (
            <>
              <span className="text-xs text-gray-600">💳 Cards</span>
            </>
          ) : null}
          {paymentMethod === 'upi' || paymentMethod === 'all' ? (
            <>
              <span className="text-xs text-gray-600">📱 UPI</span>
            </>
          ) : null}
          {paymentMethod === 'all' ? (
            <>
              <span className="text-xs text-gray-600">🏦 Netbanking</span>
              <span className="text-xs text-gray-600">👛 Wallets</span>
            </>
          ) : null}
        </div>
      </div>

      {/* Security Note */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <p className="text-xs text-gray-600">
            Your payment is secured by Razorpay. We never store your card details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RazorpayPaymentForm;

